import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { TransactionDetailsResponseDto } from "@common/dto/transactions/transaction-details-response.dto";
import {
    TRANSACTION_DETAILS_QUERY_KEY,
    useTransactionDetailsQuery,
} from "@components/transactions/TransactionDetails/useTransactionDetailsQuery";
import {
    AuditLog,
    AuditLogChangeType,
    AuditLogDiff,
} from "@common/types/auditLog";

export enum ActionType {
    CREATED = "created",
    DELETED = "deleted",
    RECONCILED = "reconciled",
    UPDATED = "updated",
}
interface DeletedTransactionData {
    description: string;
    amount: number | null;
}

interface UseTransactionForAuditLogReturn {
    transaction: TransactionDetailsResponseDto["transaction"] | undefined;
    deletedTransactionData: DeletedTransactionData | null;
    transactionDescription: string;
    actionType: ActionType | null;
}

export function useTransactionForAuditLog(
    log: AuditLog,
): UseTransactionForAuditLogReturn {
    const queryClient = useQueryClient();

    const transactionId = useMemo(() => {
        const id = Number(log.resourceId);
        return isNaN(id) ? null : id;
    }, [log.resourceId]);

    const isReconciled = useMemo(
        () =>
            log.diff?.some(
                (d: AuditLogDiff) =>
                    d.field === "reconciledInId" && d.after.value !== null,
            ),
        [log.diff],
    );

    const actionType = useMemo(() => {
        if (log.changeType === AuditLogChangeType.CREATE) {
            return ActionType.CREATED;
        }
        if (log.changeType === AuditLogChangeType.DELETE) {
            return ActionType.DELETED;
        }
        if (isReconciled) {
            return ActionType.RECONCILED;
        }
        if (log.changeType === AuditLogChangeType.UPDATE) {
            return ActionType.UPDATED;
        }
        return null;
    }, [log.changeType, isReconciled]);

    const deletedTransactionData = useMemo(() => {
        if (log.changeType !== AuditLogChangeType.DELETE) {
            return null;
        }
        const bankDescField = log.diff?.find(
            (d: AuditLogDiff) => d.field === "bankDescription",
        );
        const displayDescField = log.diff?.find(
            (d: AuditLogDiff) => d.field === "displayDescription",
        );
        const amountField = log.diff?.find(
            (d: AuditLogDiff) => d.field === "amount",
        );

        const description =
            bankDescField?.before.value ??
            displayDescField?.before.value ??
            `Transaction ${log.resourceId}`;

        const amount = amountField?.before.value
            ? Number(amountField.before.value)
            : null;

        return {
            description: description as string,
            amount: isNaN(amount as number) ? null : amount,
        };
    }, [log.changeType, log.diff, log.resourceId]);

    const cachedTransaction = useMemo(() => {
        if (log.changeType === AuditLogChangeType.DELETE || !transactionId) {
            return null;
        }

        const cachedData =
            queryClient.getQueryData<TransactionDetailsResponseDto>([
                TRANSACTION_DETAILS_QUERY_KEY,
                transactionId,
            ]);

        return cachedData?.transaction;
    }, [log.changeType, transactionId, queryClient]);

    const { transaction: fetchedTransaction } = useTransactionDetailsQuery(
        log.changeType === AuditLogChangeType.DELETE || cachedTransaction
            ? null
            : transactionId,
    );

    const transaction = cachedTransaction ?? fetchedTransaction;

    const transactionDescription = useMemo(() => {
        if (deletedTransactionData) {
            return deletedTransactionData.description;
        }
        return (
            transaction?.bankDescription ??
            transaction?.displayDescription ??
            `Transaction ${log.resourceId}`
        );
    }, [transaction, deletedTransactionData, log.resourceId]);

    return {
        transaction,
        deletedTransactionData,
        transactionDescription,
        actionType,
    };
}
